// A retained scene node presenting the same surface the map's imperative drawing
// code already uses on SVG elements: the chained helpers from `not-d3.js`
// (`append`, `attr`, `text`, `select`, `call`, `remove`) plus the handful of real
// DOM members the schemas reach for (`addEventListener`, `getBBox`, `children`,
// `parentNode`, `isConnected`).
//
// Nothing here knows how anything is drawn. It records the tree and tells the
// owning scene what changed; a backend decides what that means.

import { queryAll, queryFirst } from "./selector";

// Tags that carry accessibility or reference information rather than something to
// draw. `title` becomes a tooltip; `clipPath` and its contents are referenced by
// `clip-path` rather than painted where they sit.
const NON_RENDERING_TAGS = { title: true, desc: true, metadata: true };

export class SceneNode {
  constructor(tagName, scene) {
    this.tagName = tagName;
    this.nodeName = tagName;
    this.scene = scene;
    this.attrs = {};
    this.children = [];
    this.parentNode = null;

    this._text = null;
    this._listeners = null;

    // Filled in by the backend: the batch this node draws in, its baked
    // transform and resolved style, and the slice of the batch's vertex buffer
    // it occupies. Kept on the node so an isolated change can be re-emitted
    // without walking the tree.
    this.batch = null;
    this.geom = null;
    this.parentMatrix = null;
    this.parentStyle = null;
    this.matrix = null;
    this.style = null;
    this.vertexOffset = 0;
    this.vertexCount = 0;
  }

  get parentElement() {
    return this.parentNode;
  }

  get nonRendering() {
    return NON_RENDERING_TAGS[this.tagName] === true;
  }

  // -------------------------------------------------------------------------
  // Attributes
  // -------------------------------------------------------------------------

  attr(name, value) {
    if (value === undefined) {
      const v = this.attrs[name];
      return v === undefined ? null : v;
    }
    if (value === null) {
      this.removeAttribute(name);
      return this;
    }
    this.setAttribute(name, value);
    return this;
  }

  setAttribute(name, value) {
    const str = typeof value === "string" ? value : String(value);
    if (this.attrs[name] === str) return;
    this.attrs[name] = str;
    this.scene.nodeChanged(this, name);
  }

  getAttribute(name) {
    const v = this.attrs[name];
    return v === undefined ? null : v;
  }

  hasAttribute(name) {
    return this.attrs[name] !== undefined;
  }

  removeAttribute(name) {
    if (this.attrs[name] === undefined) return;
    delete this.attrs[name];
    this.scene.nodeChanged(this, name);
  }

  // -------------------------------------------------------------------------
  // Text
  // -------------------------------------------------------------------------

  get textContent() {
    if (this._text !== null) return this._text;
    let out = "";
    for (let i = 0; i < this.children.length; i++)
      out += this.children[i].textContent;
    return out;
  }

  set textContent(value) {
    const str = value == null ? "" : String(value);
    if (this._text === str) return;
    this._text = str;
    this.scene.nodeChanged(this, "#text");
  }

  text(value) {
    if (value === undefined) return this.textContent;
    this.textContent = value;
    return this;
  }

  // The text of a child <title>, which SVG shows as a native tooltip. Surfaced so
  // the backend can put it on the canvas' own title attribute while hovering.
  get titleText() {
    for (let i = 0; i < this.children.length; i++) {
      if (this.children[i].tagName === "title")
        return this.children[i].textContent;
    }
    const attr = this.attrs.title;
    return attr !== undefined ? attr : null;
  }

  // -------------------------------------------------------------------------
  // Structure
  // -------------------------------------------------------------------------

  append(tagName) {
    const child = new SceneNode(tagName, this.scene);
    child.parentNode = this;
    this.children.push(child);
    this.scene.structureChanged(this);
    return child;
  }

  addFirstChild(tagName) {
    const child = new SceneNode(tagName, this.scene);
    child.parentNode = this;
    this.children.unshift(child);
    this.scene.structureChanged(this);
    return child;
  }

  appendChild(child) {
    if (child.parentNode) child.parentNode.removeChild(child);
    child.parentNode = this;
    this.children.push(child);
    this.scene.structureChanged(this);
    return child;
  }

  prepend(child) {
    if (child.parentNode) child.parentNode.removeChild(child);
    child.parentNode = this;
    this.children.unshift(child);
    this.scene.structureChanged(this);
    return child;
  }

  removeChild(child) {
    const i = this.children.indexOf(child);
    if (i < 0) return child;
    this.children.splice(i, 1);
    child.parentNode = null;
    this.scene.structureChanged(this);
    this.scene.nodeDetached(child);
    return child;
  }

  remove() {
    // Silently a no-op when already detached, matching DOM Element.remove().
    // Exit animations race the protocol's own deletes - a scope's fade-out
    // finishes after the scope was torn down - and warning here turned that
    // ordinary pattern into thousands of console lines an hour.
    if (this.parentNode == null) return this;
    this.parentNode.removeChild(this);
    return this;
  }

  get isConnected() {
    let node = this;
    while (node.parentNode) node = node.parentNode;
    return node === this.scene.root;
  }

  contains(other) {
    let node = other;
    while (node) {
      if (node === this) return true;
      node = node.parentNode;
    }
    return false;
  }

  // -------------------------------------------------------------------------
  // Queries
  // -------------------------------------------------------------------------

  select(selector) {
    return queryFirst(this, selector);
  }

  selectAll(selector) {
    return queryAll(this, selector);
  }

  querySelector(selector) {
    return queryFirst(this, selector);
  }

  querySelectorAll(selector) {
    return queryAll(this, selector);
  }

  call(fn) {
    fn(this);
    return this;
  }

  // -------------------------------------------------------------------------
  // Events
  // -------------------------------------------------------------------------

  addEventListener(type, handler) {
    if (!this._listeners) this._listeners = {};
    const list = this._listeners[type] || (this._listeners[type] = []);
    if (list.indexOf(handler) < 0) list.push(handler);
  }

  removeEventListener(type, handler) {
    if (!this._listeners) return;
    const list = this._listeners[type];
    if (!list) return;
    const i = list.indexOf(handler);
    if (i >= 0) list.splice(i, 1);
  }

  listenersFor(type) {
    return this._listeners ? this._listeners[type] : undefined;
  }

  // Read by the contextmenu handlers, which add the scroll offset of whatever
  // they are attached to. The map never scrolls, and the canvas cannot.
  get scrollLeft() {
    return 0;
  }

  get scrollTop() {
    return 0;
  }

  // -------------------------------------------------------------------------
  // Geometry
  // -------------------------------------------------------------------------

  getBBox() {
    return this.scene.measure(this);
  }
}
